// @ts-nocheck
import * as noUiSlider from 'nouislider';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  Renderer2,
  forwardRef,
  Inject,
  PLATFORM_ID,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  Validator,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  FormGroup,
  FormBuilder,
  Validators,
} from '@angular/forms';
import { asapScheduler, Subscription } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { UIKitIconRegistry } from '../../icon/icon.registry';
import { editIcon } from '../../icon/edit/edit';

export type rangeEventType =
  | 'start'
  | 'slide'
  | 'update'
  | 'change'
  | 'set'
  | 'end';

export interface RangeSplit {
  from: number;
  to: number;
  color: string;
}

export interface RangeOptions extends noUiSlider.Options {
  label: string;
  minLabel?: string;
  maxLabel?: string;
  split?: RangeSplit[];
  displayFormat?: (v: any) => string;
}

@Component({
  selector: 'lib-range',
  templateUrl: './range.component.html',
  styleUrls: ['./range.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RangeComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => RangeComponent),
      multi: true,
    },
  ],
  exportAs: 'libRange',
  // changeDetection: ChangeDetectionStrategy.OnPush
})
export class RangeComponent
implements ControlValueAccessor, Validator, OnInit, OnDestroy {
  static EVENTS: rangeEventType[] = [
    'start',
    'slide',
    'update',
    'change',
    'set',
    'end',
  ];

  @ViewChild('range', { static: true }) range: ElementRef;

  @ViewChild('firstInput') firstInput: ElementRef;

  @ViewChild('secondInput') secondInput: ElementRef;

  // tslint:disable-next-line: no-output-native
  @Output() start: EventEmitter<string | string[]> = new EventEmitter();

  // tslint:disable-next-line: no-output-native
  @Output() slide: EventEmitter<string | string[]> = new EventEmitter();

  // tslint:disable-next-line: no-output-native
  @Output() update: EventEmitter<string | string[]> = new EventEmitter();

  // tslint:disable-next-line: no-output-native
  @Output() change: EventEmitter<string | string[]> = new EventEmitter();

  // tslint:disable-next-line: no-output-native
  @Output() set: EventEmitter<string | string[]> = new EventEmitter();

  // tslint:disable-next-line: no-output-native
  @Output() end: EventEmitter<string | string[]> = new EventEmitter();

  @Input()
  set options(options: Partial<RangeOptions>) {
    if (!this.isBrowser) {
      return;
    }

    if (this.noUiSlider) {
      this.noUiSlider.updateOptions(options);
    } else {
      this.noUiSlider = noUiSlider.create(
        this.range.nativeElement,
        options as noUiSlider.Options,
      );

      for (const event of RangeComponent.EVENTS) {
        this.noUiSlider.on(event, (values) => this.onChange(event));
      }
    }

    this.label = options.label;

    if (options.displayFormat) {
      this.displayFormat = options.displayFormat;

      // checking if displayFormat cuts three zeros at the end
      if (this.displayFormat(1000).replace(/\D/g, '') === '1') {
        this.multiplier = 1000;
      }
    }

    if (options.minLabel) {
      this.minLabel = options.minLabel;
    }

    if (options.maxLabel) {
      this.maxLabel = options.maxLabel;
    }

    let rangeValidators = [];
    if (options.range) {
      if ('min' in options.range && typeof options.range.min === 'number') {
        this.minValue = options.range.min / this.multiplier;
        rangeValidators = [...rangeValidators, Validators.min(options.range.min)];
      }
      if ('max' in options.range && typeof options.range.max === 'number') {
        this.maxValue = options.range.max / this.multiplier;
        rangeValidators = [...rangeValidators, Validators.max(options.range.max)];
      }
    }

    if ('step' in options && typeof options.step === 'number') {
      this.stepValue = options.step / this.multiplier;
    }

    const validators = Validators.compose([...rangeValidators]);
    this.editModeFormGroup = this.formBuilder.group(this.isArray
      ? {
        firstValue: [{ value: this.value[0] }, validators],
        secondValue: [{ value: this.value[1] }, validators],
      }
      : {
        firstValue: [{ value: this.value }, validators],
        secondValue: [{ value: '' }, validators],
      });
  }

  @Input() propagateChangeOn: rangeEventType[] = ['change'];

  @Input() theme: 'primary' | 'warn';

  @Input()
  set value(values: string | string[]) {
    if (!this.noUiSlider || !this.isBrowser) {
      return;
    }

    const range = (
      Array.isArray(values)
        ? values.map((value) => +value)
        : values
    ) as number | number[];

    if (this.noUiSlider) {
      this.noUiSlider.set(range);
    }
  }

  get value(): string | string[] {
    return !!this.noUiSlider
      && this.isBrowser
      ? this.noUiSlider.get()
      : '';
  }

  get displayValue(): string[] {
    const value = !!this.noUiSlider && this.isBrowser
      ? this.noUiSlider.get()
      : '';

    return (Array.isArray(value)
      ? value
      : [value])
      .map(this.displayFormat);
    // .join(' – ');
  }

  get isArray(): boolean {
    return Array.isArray(this.value);
  }

  get class(): string {
    return `lib-range${this.theme ? `-${this.theme}` : ''}`;
  }

  @Input() label: string;

  @Input() minLabel: string;

  @Input() maxLabel: string;

  noUiSlider: noUiSlider.noUiSlider;

  isEditModeEnabled: boolean;

  editModeFormGroup: FormGroup;

  editModeSubscription: Subscription;

  isDisabled: boolean;

  minValue: number;

  maxValue: number;

  stepValue: number;

  private isBrowser: boolean;

  private delayBlur: Subscription;

  private multiplier = 1;

  constructor(
    @Inject(PLATFORM_ID) platformId,
    private formBuilder: FormBuilder,
    iconRegistry: UIKitIconRegistry,
    private renderer: Renderer2,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    iconRegistry.registerIcons([editIcon]);
  }

  ngOnInit(): void {
    this.editModeSubscription = this.editModeFormGroup.valueChanges
      .subscribe(this.onEditModeValueChange.bind(this));
  }

  ngOnDestroy(): void {
    if (this.editModeSubscription) {
      this.editModeSubscription.unsubscribe();
      this.editModeSubscription = void 0;
    }

    if (this.noUiSlider && this.isBrowser) {
      for (const event of RangeComponent.EVENTS) {
        this.noUiSlider.off(event);
      }
    }

    this.noUiSlider = void 0;
  }

  writeValue(values: string | string[]): void {
    this.value = values;
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void { }

  setDisabledState(isDisabled: boolean) {
    this.isDisabled = isDisabled;
    this.renderer.setProperty(
      this.range?.nativeElement,
      'disabled',
      isDisabled,
    );
    if (isDisabled) {
      this.editModeFormGroup.disable();
    } else {
      this.editModeFormGroup.enable();
    }
  }

  onChange(trigger: rangeEventType) {
    if (this.propagateChangeOn) {
      if (this.propagateChangeOn.indexOf(trigger) > -1) {
        this.propagateChange(this.value);
      }
    } else if (trigger === 'change') {
      this.propagateChange(this.value);
    }

    this[trigger].emit(this.value);
  }

  validate(control: AbstractControl): { [key: string]: any } | null {
    return null;
  }

  private propagateChange = (_: any) => { };

  /**
   * on key down, exit edit mode
   */
  public onKeyDown(event: KeyboardEvent): void {
    const target = (event.target as HTMLElement);

    if (event.key === 'Enter' || event.code === 'Enter') {
      event.stopImmediatePropagation();
      event.preventDefault();

      if (target) {
        target.blur();
      }
    }
  }

  public focus(event: Event): void {
    this.clearDelayBlur();
  }

  public blur(event: Event): void {
    const target = (event.target as HTMLElement);

    if (target) {
      target.blur();
    }

    this.delayBlur = asapScheduler
      .schedule(() => this.exitEditMode(), 100);
  }

  /**
   * enter edit mode
   * @param event mouse event
   * @param focusInput (optional) input element to set focus - defaults to first input
   */
  public enterEditMode(event?: MouseEvent, focusInput: HTMLInputElement = this.firstInput.nativeElement) {
    if (event) {
      event.stopImmediatePropagation();
    }

    if (this.isDisabled) {
      return;
    }

    this.isEditModeEnabled = true;

    if (this.isArray) {
      this.editModeFormGroup.patchValue({
        firstValue: `${+this.value[0] / this.multiplier}`,
        secondValue: `${+this.value[1] / this.multiplier}`,
      }, { emitEvent: false });
    } else {
      this.editModeFormGroup.patchValue({
        firstValue: `${+this.value / this.multiplier}`,
        secondValue: '',
      }, { emitEvent: false });
    }

    focusInput.focus();
    if (focusInput) {
      asapScheduler
        .schedule(() => focusInput.focus(), 100);
    }
  }

  /**
   * exit edit mode
   */
  public exitEditMode(event?: MouseEvent | TouchEvent): void {
    this.isEditModeEnabled = false;

    this.onChange('change');

    this.clearDelayBlur();
  }

  private onEditModeValueChange(value: { firstValue: string, secondValue: string; }): void {
    const valueA = (+value.firstValue * this.multiplier).toString();

    if (this.isArray) {
      const valueB = (+value.secondValue * this.multiplier).toString();

      this.value = [valueA, valueB];
    } else {
      this.value = valueA;
    }
  }

  private clearDelayBlur(): void {
    if (this.delayBlur) {
      this.delayBlur.unsubscribe();
      this.delayBlur = void 0;
    }
  }

  private displayFormat: (v: any) => string = (v) => v;
}
